import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Divider, Radio, Form } from 'antd';
import { Checkbox } from '../../../Checkbox';
import { Label } from 'ui/Label';
import { InputNumber } from 'ui/InputNumber';
import { Select, SelectOption } from 'ui/Select';
import { StyledApplicationProductsForm } from './style';

import {
  Listing,
  ApplicationProductData,
  FarmQualityMark,
  StorageUnit,
  ListingProduct,
  FarmProduct,
} from '../../../../types/ListingData';
import {
  QualityMarkOption,
  findQualityMark,
  hasAllRequiredQualityMarks,
  hasRequiredQualityMark,
} from 'utils/qualityMarkHelpers';

interface NewApplicationProductsFormProps {
  listingData?: Listing | null;
  farmQualityMarks?: FarmQualityMark[];
  storageUnits?: StorageUnit[];
  availableProducts: { [categoryId: number]: FarmProduct[] };
  allQualityMarks?: QualityMarkOption[];
}

const normalizeQuantity = (
  quantity: number | undefined | null
): number | undefined => {
  return quantity && quantity > 0 ? quantity : undefined;
};

export const NewApplicationProductsForm = ({
  listingData,
  farmQualityMarks = [],
  storageUnits = [],
  availableProducts,
  allQualityMarks,
}: NewApplicationProductsFormProps) => {
  const intl = useIntl();
  const form = Form.useFormInstance();

  const handleProductChange = useCallback(
    (
      index: number,
      field: keyof ApplicationProductData,
      value: string | number | undefined
    ) => {
      const currentProducts = (form.getFieldValue('application_products') ||
        []) as ApplicationProductData[];
      const listingProductId = listingData?.products?.[index]?.id;

      const currentProduct = currentProducts[index] || {
        listing_product_id: listingProductId,
      };

      const updatedProduct = {
        ...currentProduct,
        [field]: value,
      };

      if (listingProductId && !updatedProduct.listing_product_id) {
        updatedProduct.listing_product_id = listingProductId;
      }

      if (field === 'offer_type') {
        if (value === 'full_amount') {
          updatedProduct.offered_quantity = undefined;
          form.setFieldValue(
            ['application_products', index, 'offered_quantity'],
            undefined
          );
        } else if (value === 'other_amount') {
          updatedProduct.offered_quantity = normalizeQuantity(
            updatedProduct.offered_quantity
          );
        }
      }

      if (field === 'offered_quantity') {
        if (value === 0 || value === null || value === '') {
          updatedProduct.offered_quantity = undefined;
        }
      }

      if (field === 'offered_price') {
        if (value === 0 || value === null || value === '') {
          updatedProduct.offered_price = undefined;
        }
      }

      form.setFieldValue(['application_products', index], updatedProduct);
    },
    [form, listingData?.products]
  );

  const formatProductDisplayName = (product: FarmProduct): string => {
    const qualityMarkNames = Array.from(
      new Set(
        (product.qualitymarks ?? [])
          .map((qm) => qm.quality_mark?.name ?? '')
          .filter(Boolean)
      )
    );
    const qualityMarks =
      qualityMarkNames.length > 0 ? ` (${qualityMarkNames.join(', ')})` : '';
    return `${product.name}${qualityMarks}`;
  };

  const handleProductSelection = useCallback(
    (index: number, productId: number) => {
      handleProductChange(index, 'farm_product_id', productId);
    },
    [handleProductChange]
  );

  const getProductQualityMarkOptions = useCallback(
    (product: ListingProduct, selectedProduct?: FarmProduct) => {
      if (
        !product.quality_mark_ids ||
        !Array.isArray(product.quality_mark_ids)
      ) {
        return [];
      }

      const selectedProductQualityMarkIds = selectedProduct?.qualitymarks
        ? selectedProduct.qualitymarks.map((qm) => qm.quality_mark_id)
        : [];

      return product.quality_mark_ids.map((id) => {
        const qmark = findQualityMark(allQualityMarks, id);
        const hasProductQualityMark = hasRequiredQualityMark(
          id,
          selectedProductQualityMarkIds,
          allQualityMarks
        );
        return {
          value: id,
          label: qmark?.name || '',
          disabled: true,
          checked: true,
          className: hasProductQualityMark
            ? 'has-quality-mark'
            : 'no-quality-mark',
        };
      });
    },
    [allQualityMarks]
  );

  const isProductMissingQualityMarks = useCallback(
    (
      listingProduct: ListingProduct,
      selectedFarmProduct?: FarmProduct
    ): boolean => {
      if (!selectedFarmProduct || !listingProduct.quality_mark_ids) {
        return false;
      }

      const requiredQualityMarkIds = listingProduct.quality_mark_ids;
      if (requiredQualityMarkIds.length === 0) {
        return false;
      }

      const selectedProductQualityMarkIds = selectedFarmProduct.qualitymarks
        ? selectedFarmProduct.qualitymarks.map((qm) => qm.quality_mark_id)
        : [];

      return !hasAllRequiredQualityMarks(
        requiredQualityMarkIds,
        selectedProductQualityMarkIds,
        allQualityMarks
      );
    },
    [allQualityMarks]
  );

  if (!listingData?.products) {
    return (
      <div className="text-center">
        <Label
          label={intl.formatMessage({
            id: 'listings.application.no_products_available',
          })}
        />
      </div>
    );
  }

  return (
    <StyledApplicationProductsForm>
      <Form.List name="application_products">
        {(fields) => {
          return (
            <>
              {listingData.products!.map((listingProduct, index) => {
                const field = fields[index];
                if (!field) {
                  return null;
                }

                const currentProducts = (form.getFieldValue(
                  'application_products'
                ) || []) as ApplicationProductData[];
                const product = currentProducts[index];
                const selectedProduct =
                  product?.farm_product_id && listingProduct.category_id
                    ? availableProducts[listingProduct.category_id]?.find(
                        (p) => p.id === product.farm_product_id
                      )
                    : undefined;
                const productQualityMarkOptions = getProductQualityMarkOptions(
                  listingProduct,
                  selectedProduct
                );

                const getDisplayValue = (
                  listingProp: keyof ListingProduct
                ): unknown => {
                  return listingProduct[listingProp];
                };

                const getStorageUnitShort = (): string => {
                  const storageUnit = listingProduct.storage_unit;
                  if (storageUnit && 'short' in storageUnit) {
                    return storageUnit.short || '';
                  }
                  return '';
                };

                return (
                  <div key={listingProduct.id} className="product-item">
                    <Form.Item
                      name={[field.name, 'listing_product_id']}
                      initialValue={listingProduct.id}
                      hidden
                    >
                      <input type="hidden" />
                    </Form.Item>

                    <div className="product-header">
                      <Label label={index + 1} className="product-number" />
                      <div className="product-title">
                        <Label
                          label={`${listingProduct.name || listingProduct.category?.name} (${intl.formatMessage(
                            {
                              id: 'listings.application.quantity',
                            }
                          )}: ${getDisplayValue('required_quantity')} ${getStorageUnitShort()}.${
                            getDisplayValue('allow_smaller_quantity')
                              ? `, ${intl.formatMessage({
                                  id: 'listings.application.min_quantity',
                                })}: ${getDisplayValue('min_acceptable_offer_quantity')} ${getStorageUnitShort()}.`
                              : ''
                          })`}
                        />
                      </div>
                      {productQualityMarkOptions.length > 0 && (
                        <div className="quality-marks">
                          {productQualityMarkOptions.map((qmarkOption) => (
                            <div
                              key={qmarkOption.value}
                              className="quality-mark-item"
                            >
                              <Checkbox
                                checked={qmarkOption.checked}
                                disabled={qmarkOption.disabled}
                                className={qmarkOption.className}
                              />
                              <Label
                                className="quality-mark-label"
                                label={qmarkOption.label}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="product-form">
                      <Form.Item
                        shouldUpdate={(prev, curr) => {
                          const prevOfferType =
                            prev?.application_products?.[index]?.offer_type;
                          const currOfferType =
                            curr?.application_products?.[index]?.offer_type;
                          return prevOfferType !== currOfferType;
                        }}
                      >
                        {() => {
                          const offerType =
                            form.getFieldValue([
                              'application_products',
                              index,
                              'offer_type',
                            ]) || 'full_amount';

                          return (
                            <div className="offer-type">
                              <Radio.Group
                                value={offerType}
                                onChange={(e) =>
                                  handleProductChange(
                                    index,
                                    'offer_type',
                                    e.target.value
                                  )
                                }
                              >
                                <div className="radio-options">
                                  <Radio value="full_amount">
                                    {intl.formatMessage({
                                      id: 'listings.application.can_offer_full_amount',
                                    })}
                                  </Radio>
                                  {!!getDisplayValue(
                                    'allow_smaller_quantity'
                                  ) && (
                                    <Radio value="other_amount">
                                      {intl.formatMessage({
                                        id: 'listings.application.other_amounts',
                                      })}
                                    </Radio>
                                  )}
                                </div>
                              </Radio.Group>
                              {!getDisplayValue('allow_smaller_quantity') && (
                                <Label
                                  label={intl.formatMessage({
                                    id: 'listings.application.no_other_amounts',
                                  })}
                                />
                              )}
                            </div>
                          );
                        }}
                      </Form.Item>

                      <Form.Item
                        name={[field.name, 'offer_type']}
                        initialValue="full_amount"
                        hidden
                      >
                        <input type="hidden" />
                      </Form.Item>

                      <Divider />

                      <div className="row">
                        <div className="product-selection">
                          <div className="product-display">
                            <Select
                              name={[field.name, 'farm_product_id']}
                              label={intl.formatMessage({
                                id: 'listings.application.product',
                              })}
                              required
                              value={product?.farm_product_id || undefined}
                              onChange={(value) =>
                                handleProductSelection(index, value)
                              }
                              placeholder={intl.formatMessage({
                                id: 'listings.application.select_product',
                              })}
                              allowClear
                              optionLabelProp="label"
                              showSearch
                            >
                              {(
                                availableProducts[listingProduct.category_id] ||
                                []
                              ).map((product) => (
                                <SelectOption
                                  key={product.id}
                                  value={product.id}
                                  label={formatProductDisplayName(product)}
                                >
                                  {formatProductDisplayName(product)}
                                </SelectOption>
                              ))}
                            </Select>
                            {selectedProduct &&
                              isProductMissingQualityMarks(
                                listingProduct,
                                selectedProduct
                              ) && (
                                <Label
                                  label={intl.formatMessage({
                                    id: 'validation.certificates_required_for_missing_quality_marks',
                                  })}
                                  className="product-warning-label"
                                />
                              )}
                          </div>
                        </div>

                        <Form.Item
                          noStyle
                          shouldUpdate={(prev, curr) => {
                            const prevOfferType =
                              prev?.application_products?.[index]?.offer_type;
                            const currOfferType =
                              curr?.application_products?.[index]?.offer_type;
                            return prevOfferType !== currOfferType;
                          }}
                        >
                          {() => {
                            const offerType =
                              form.getFieldValue([
                                'application_products',
                                index,
                                'offer_type',
                              ]) || 'full_amount';

                            if (
                              offerType === 'other_amount' &&
                              !!getDisplayValue('allow_smaller_quantity')
                            ) {
                              const minQuantity =
                                listingProduct.min_acceptable_offer_quantity &&
                                listingProduct.min_acceptable_offer_quantity > 0
                                  ? listingProduct.min_acceptable_offer_quantity
                                  : undefined;

                              return (
                                <div className="quantity-and-unit">
                                  <InputNumber
                                    name={[field.name, 'offered_quantity']}
                                    required
                                    min={minQuantity}
                                    className="quantity-input"
                                    label={intl.formatMessage({
                                      id: 'listings.application.quantity',
                                    })}
                                    controls={false}
                                  />
                                  <Select
                                    name={[field.name, 'offered_unit_id']}
                                    required
                                    initialValue={
                                      form.getFieldValue([
                                        'application_products',
                                        index,
                                        'offered_unit_id',
                                      ]) || listingProduct.storage_unit?.id
                                    }
                                    placeholder=""
                                    allowClear
                                    optionLabelProp="label"
                                  >
                                    {storageUnits.map((unit) => (
                                      <SelectOption
                                        key={unit.id}
                                        value={unit.id}
                                        label={unit.short || unit.name}
                                      >
                                        {unit.name}{' '}
                                        {unit.short ? `(${unit.short})` : ''}
                                      </SelectOption>
                                    ))}
                                  </Select>
                                </div>
                              );
                            }
                            return null;
                          }}
                        </Form.Item>
                      </div>

                      <div className="row">
                        <div className="quantity-and-unit">
                          <InputNumber
                            name={[field.name, 'offered_price']}
                            required
                            className="quantity-input"
                            label={intl.formatMessage({
                              id: 'listings.application.price_eur',
                            })}
                            controls={false}
                          />
                          <Select
                            name={[field.name, 'offered_price_unit_id']}
                            required
                            initialValue={
                              form.getFieldValue([
                                'application_products',
                                index,
                                'offered_price_unit_id',
                              ]) || listingProduct.storage_unit?.id
                            }
                            placeholder=""
                            allowClear
                            optionLabelProp="label"
                          >
                            {storageUnits.map((unit) => (
                              <SelectOption
                                key={unit.id}
                                value={unit.id}
                                label={unit.short || unit.name}
                              >
                                {unit.name}{' '}
                                {unit.short ? `(${unit.short})` : ''}
                              </SelectOption>
                            ))}
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          );
        }}
      </Form.List>
    </StyledApplicationProductsForm>
  );
};
